import React, { useContext, useState, useEffect } from 'react';
import { FaBriefcase, FaRegClock, FaRegUser } from 'react-icons/fa';
import { GiNetworkBars } from 'react-icons/gi';
import { useNavigate } from 'react-router-dom';
import { BaseURL } from '../../../Files/BaseURL';
import AlertContext from '../../../Context/Alert/AlertContext';

const Overview = () => {
    const { showAlert } = useContext(AlertContext);
    const [revenueData, setRevenueData] = useState(null);
    const [projectData, setProjectData] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [period, setPeriod] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState(''); // Default to no filter
    const navigate = useNavigate();

    const fetchDashboardData = async (selectedPeriod) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('p@token');
            let response, result;

            if (selectedPeriod === '') {
                // Fetch monthly data from three APIs
                const revenueResponse = await fetch(`${BaseURL}/api/bussniess/totalRevenue`, {
                    method: 'GET',
                    headers: {
                        'auth-token': token,
                        'Content-Type': 'application/json',
                    },
                });
                const projectsResponse = await fetch(`${BaseURL}/api/bussniess/totalProjects`, {
                    method: 'GET',
                    headers: {
                        'auth-token': token,
                        'Content-Type': 'application/json',
                    },
                });
                const clientsResponse = await fetch(`${BaseURL}/api/bussniess/totalClients`, {
                    method: 'GET',
                    headers: {
                        'auth-token': token,
                        'Content-Type': 'application/json',
                    },
                });

                if (!revenueResponse.ok || !projectsResponse.ok || !clientsResponse.ok) {
                    showAlert('Failed to fetch dashboard data', 'danger');
                    return;
                }

                const revenueResult = await revenueResponse.json();
                const projectsResult = await projectsResponse.json();
                const clientsResult = await clientsResponse.json();
                if (revenueResult.success && projectsResult.success && clientsResult.success) {
                    setPeriod('month');
                    setRevenueData({
                        overall: { totalRevenue: revenueResult.data.overall.totalRevenue },
                        currentMonth: { totalRevenue: revenueResult.data.currentMonth.totalRevenue },
                        percentageChange: revenueResult.data.percentageChange,
                        trend: revenueResult.data.trend,
                    });
                    setProjectData({
                        overallProjects: projectsResult.data.totalProjects,
                        currentMonthProjects: projectsResult.data.currentMonthProjects,
                        percentageChange: projectsResult.data.percentageChange,
                        trend: projectsResult.data.trend,
                    });
                    setClientData({
                        overall: { totalClients: clientsResult.data.overall.totalClients },
                        currentMonth: { totalClients: clientsResult.data.currentMonth.totalClients },
                        percentageChange: clientsResult.data.percentageChange,
                        trend: clientsResult.data.trend,
                    });
                }


                else {
                    showAlert('API request failed', 'danger');
                }
            } else {
                // Fetch filtered data from /dashboard/filterMetrics
                response = await fetch(`${BaseURL}/api/bussniess//dashboard/filterMetrics?period=${selectedPeriod}`, {
                    method: 'GET',
                    headers: {
                        'auth-token': token,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    showAlert('Failed to fetch dashboard data', 'danger');
                    return;
                }

                result = await response.json();
                if (result.success) {
                    setPeriod(result.data.period);
                    const { data } = result;
                    const { revenue, projects, clients } = result.data;

                    setRevenueData({
                        overall: { totalRevenue: revenue.overall.totalRevenue },
                        currentPeriod: { totalRevenue: revenue.currentPeriod.totalRevenue },
                        percentageChange: revenue.percentageChange,
                        trend: revenue.trend,
                    });

                    setProjectData({
                        overallProjects: projects.overallProjects.total,
                        currentPeriodProjects: projects.currentPeriodProjects,
                        percentageChange: projects.percentageChange,
                        trend: projects.trend,
                    });

                    setClientData({
                        overall: { totalClients: clients.overall.totalClients },
                        currentPeriod: { totalClients: clients.currentPeriod.totalClients },
                        percentageChange: clients.percentageChange,
                        trend: clients.trend,
                    });
                } else {
                    showAlert(result.message || 'API request failed', 'danger');
                }
            }
        } catch (err) {
            showAlert(err.message, 'danger');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData(selectedPeriod);
    }, [selectedPeriod]);

    const handlePeriodChange = (e) => {
        setSelectedPeriod(e.target.value);
    };

    const formatRevenue = (revenue) => {
        return `$${revenue?.toLocaleString('en-US')}`;
    };

    const formatPercentageChange = (percentage, trend, period) => {
        return `${percentage}% ${trend} from ${period === 'month' ? 'last month' : 'last period'}`;
    };



    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4 font-pop justify-between">
                <h2 className="text-xl font-medium">Overview</h2>
                <div className="flex items-center gap-4">
                    <button
                        className="text-base md:text-lg self-end md:self-auto border-2 bg-transparent font-medium text-primarygreen border-primarygreen py-2 px-4 rounded-xl hover:bg-primarygreen hover:text-white ease-in-out duration-300"
                        onClick={() => navigate('/user-dashboard/withdraw-history')}
                    >
                        Recent History
                    </button>
                    <select
                        value={selectedPeriod}
                        onChange={handlePeriodChange}
                        className="bg-white outline-none border-none rounded-2xl py-2 px-4 font-pop"
                    >
                        <option value="">All</option>
                        <option value="2days">Last 2 days</option>
                        <option value="7days">Last 7 days</option>
                        <option value="30days">Last 30 days</option>
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                <div className="flex flex-col gap-2 bg-white/50 rounded-xl font-pop py-4 px-4">
                    <div className="bg-[#D398E7] w-fit rounded-full p-2 text-white text-xl">
                        <GiNetworkBars />
                    </div>
                    <h4 className="text-lightGrey">Total revenue</h4>
                    <h1 className="text-lg font-medium">

                        {loading
                            ? 'Loading...'
                            : revenueData
                                ? formatRevenue(period === 'month' ? revenueData.currentMonth.totalRevenue : revenueData.currentPeriod.totalRevenue)
                                : '$0'}

                    </h1>
                    <p className="text-sm">
                        {
                            loading
                                ? 'Loading...'
                                : revenueData
                                    ? formatPercentageChange(revenueData.percentageChange, revenueData.trend, period)
                                    : '0% no change from last period'}
                    </p>
                </div>
                <div className="flex flex-col gap-2 bg-white/50 rounded-xl font-pop py-4 px-4">
                    <div className="bg-[#E89271] w-fit rounded-full p-2 text-white text-xl">
                        <FaBriefcase />
                    </div>
                    <h4 className="text-lightGrey">Projects</h4>
                    <h1 className="text-lg font-medium">
                        {loading
                            ? 'Loading...'
                            : projectData
                                ? (period === 'month' ? projectData.overallProjects : projectData.currentPeriodProjects)
                                : 0}
                    </h1>
                    <p className="text-sm">
                        {loading
                            ? 'Loading...'
                            : projectData
                                ? formatPercentageChange(projectData.percentageChange, projectData.trend, period)
                                : '0% no change from last period'}
                    </p>
                </div>
                <div className="flex flex-col gap-2 bg-white/50 rounded-xl font-pop py-4 px-4">
                    <div className="bg-[#E89271] w-fit rounded-full p-2 text-white text-xl">
                        <FaRegClock />
                    </div>
                    <h4 className="text-lightGrey">Time Spent</h4>
                    <h1 className="text-lg font-medium">20 Hrs/day</h1>
                    <p className="text-sm">8% increase from last month</p>
                </div>
                <div className="flex flex-col gap-2 bg-white/50 rounded-xl font-pop py-4 px-4">
                    <div className="bg-[#E89271] w-fit rounded-full p-2 text-white text-xl">
                        <FaRegUser />
                    </div>
                    <h4 className="text-lightGrey">Total Clients</h4>
                    <h1 className="text-lg font-medium">
                        {loading
                            ? 'Loading...'
                            : formatRevenue(
                                period === 'month'
                                    ? clientData?.overall?.totalClients
                                    : clientData?.currentPeriod?.totalClients
                            ) || '0'}
                    </h1>

                    <p className="text-sm">
                        {
                            loading
                                ? 'Loading...'
                                : clientData
                                    ? formatPercentageChange(clientData.percentageChange, clientData.trend, period)
                                    : '0% no change from last period'}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Overview;
